import React, { useState } from "react";
import { motion } from "framer-motion";
import { Modal, Button, Text } from "@mantine/core";
import "./contact-form.css";
import { useNavigate } from "react-router";

const ContactForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    subject: "",
    issueCategory: "",
    message: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [formTouched, setFormTouched] = useState({
    subject: false,
    issueCategory: false,
    message: false,
  });

  const [modalOpened, setModalOpened] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const API_ENDPOINT = "/wp-json/dbsb/v1/contact/general";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setFormTouched((prev) => ({ ...prev, [name]: true }));
    validateForm();
  };

  const validateForm = () => {
    const newErrors = {};
    const touched = formTouched;

    if (touched.subject && !formData.subject) {
      newErrors.subject = "Subject is required";
    }
    if (touched.issueCategory && !formData.issueCategory) {
      newErrors.issueCategory = "Issue category is required";
    }
    if (touched.message && !formData.message) {
      newErrors.message = "Message is required";
    }

    setFormErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const isFormIncomplete = () => {
    return Object.values(formData).some((value) => value === "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormTouched({
      subject: true,
      issueCategory: true,
      message: true,
    });

    if (!validateForm()) return;

    setIsLoading(true);
    setApiError(null);

    try {
      if (!window.wpApiSettings?.nonce) {
        throw new Error("Authentication token not found. Please try again.");
      }

      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-WP-Nonce": window.wpApiSettings.nonce,
        },
        credentials: "same-origin",
        body: JSON.stringify({
          subject: formData.subject,
          category: formData.issueCategory,
          message: formData.message,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to send contact form.");
      }

      setModalOpened(true);
      setFormData({
        subject: "",
        issueCategory: "",
        message: "",
      });
      setFormTouched({
        subject: false,
        issueCategory: false,
        message: false,
      });
    } catch (error) {
      setApiError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      backgroundColor: "#2E354F",
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
  };

  return (
    <div className="contact-form-container">
      <motion.div
        className="contact-form-content"
        variants={formVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h2 variants={itemVariants}>Contact form</motion.h2>
        <motion.div className="contact-form-card" variants={itemVariants}>
          <motion.form
            onSubmit={handleSubmit}
            className="contact-form"
            variants={formVariants}
          >
            <h3>Fill below</h3>
            <motion.div variants={itemVariants} className="form-group">
              <label>Subject</label>
              <input
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Short title for the issue"
              />
              {formErrors.subject && (
                <span className="error">{formErrors.subject}</span>
              )}
            </motion.div>
            <motion.div variants={itemVariants} className="form-group">
              <label>Category</label>
              <select
                name="issueCategory"
                value={formData.issueCategory}
                onChange={handleChange}
                onBlur={handleBlur}
              >
                <option value="">Select from dropdown</option>
                <option value="result_issue">Result issue</option>
                <option value="payment_issue">Payment issue</option>
                <option value="other">Other</option>
              </select>
              {formErrors.issueCategory && (
                <span className="error">{formErrors.issueCategory}</span>
              )}
            </motion.div>
            <motion.div variants={itemVariants} className="form-group">
              <label>Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter the issue here"
                rows="4"
              />
              {formErrors.message && (
                <span className="error">{formErrors.message}</span>
              )}
            </motion.div>
          </motion.form>
        </motion.div>
        {apiError && (
          <motion.p className="error-message" variants={itemVariants}>
            {apiError}
          </motion.p>
        )}
        <motion.div variants={itemVariants} className="button-group">
          <motion.button
            type="submit"
            onClick={handleSubmit}
            className={`submit-button ${
              isLoading ||
              isFormIncomplete() ||
              Object.values(formErrors).some((error) => error)
                ? "disabled"
                : ""
            }`}
            disabled={
              isLoading ||
              isFormIncomplete() ||
              Object.values(formErrors).some((error) => error)
            }
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
          >
            {isLoading ? "Submitting..." : "Submit"}
          </motion.button>
        </motion.div>
      </motion.div>

      {/* Success Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        closeOnEscape={false}
        closeOnClickOutside={false}
        withCloseButton={false}
        centered
        size="sm"
        className="success-modal"
        overlayProps={{
          opacity: 0.55,
          blur: 3,
        }}
      >
        <div className="modal-content">
          <div className="modal-message-box">
            <Text className="modal-title">Support request submitted</Text>
            <Text className="modal-message">
              Your request has been submitted. A support representative will get back to you shortly.
            </Text>
            <Button
              component="a"
              href="/dashboard"
              className="modal-close-button"
              style={{ height: "fit-content" }}
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ContactForm;